// Sugalarm — web command-station frame (cycle 170).
//
// KioskFrame delegates here when variant === "web" (the cycle-25 mobile
// precedent, where it delegates to MobileFrame). Layout: a persistent
// left nav rail + a live household status strip pinned above a scrolling
// content canvas that hosts the existing web screens unchanged. Kiosk and
// mobile keep their own chrome and never render this component.
//
// When `showChrome` is false (the login screen) the shell collapses to a
// bare full-viewport canvas — the rail + strip only belong to the signed-
// in command station, matching the pre-cycle web frame's chrome-off path.

import { type ReactNode } from "react";

import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import type { TTSState } from "../../hooks/useTTS";
import type { Member, User, Zone } from "../../lib/types";
import { SpeakingBanner } from "../voice/SpeakingBanner";
import { ThemeBackground } from "../ThemeBackground";
import { HouseholdStrip } from "./HouseholdStrip";
import { WebNavRail } from "./WebNavRail";
import styles from "./WebFrame.module.css";

interface Props {
  user: User | null;
  socket: GlucoseSocketState;
  now: number;
  units: "mg/dL" | "mmol/L";
  /** Accessible member set (owned ∪ linked) — drives the status strip. */
  members: Member[];
  activeMemberId: string | null;
  onSelectMember: (memberId: string) => void;
  showChrome: boolean;
  /** Operator broadcast banner (already gated to non-null in App). */
  broadcastBanner: ReactNode;
  /** Cycle 222: TTS handle + current zone + episode-level urgent — drive the
   *  transient bottom SpeakingBanner pinned to the content region. */
  tts: TTSState;
  zone: Zone;
  urgentActive: boolean;
  /** Cycle 452: the compact alarm control. Rendered in BOTH paths — it is
   *  not behind the chrome gate — in the banner position, above the canvas. */
  alarmControl?: ReactNode;
  children: ReactNode;
}

export function WebFrame({
  user,
  socket,
  now,
  units,
  members,
  activeMemberId,
  onSelectMember,
  showChrome,
  broadcastBanner,
  tts,
  zone,
  urgentActive,
  alarmControl = null,
  children,
}: Props) {
  // Chrome-off (login): a bare full-viewport canvas, no rail/strip.
  if (!showChrome) {
    return (
      <div className={styles.bareRoot} data-overlay-host="">
        <ThemeBackground />
        {alarmControl}
        <div className={styles.bareCanvas}>{children}</div>
      </div>
    );
  }

  return (
    <div className={styles.root} data-overlay-host="">
      {/* Cycle 84: uploaded background-image layer (behind all content). */}
      <ThemeBackground />
      <WebNavRail user={user} unackCount={socket.unackCount} />
      <div className={styles.main}>
        <HouseholdStrip
          members={members}
          socket={socket}
          now={now}
          units={units}
          activeMemberId={activeMemberId}
          onSelect={onSelectMember}
        />
        {broadcastBanner}
        {alarmControl}
        <div className={styles.canvas}>{children}</div>
      </div>
      {/* Cycle 222 / 222.1: transient bottom "speaking" banner. Mounted as a
          child of `.root` (the full-viewport positioned container) — NOT
          `.main` — so it spans the FULL device width edge-to-edge, running
          under the left nav rail's lower edge too. Web has no bottom nav, so
          it hugs the bottom edge. */}
      <SpeakingBanner
        tts={tts}
        zone={zone}
        urgentActive={urgentActive}
        variant="web"
        bottom={0}
        left={0}
      />
    </div>
  );
}
